import {
    ChangeDetectionStrategy,
    Component,
    computed,
    inject,
    Signal,
    signal,
    viewChild,
    WritableSignal
} from "@angular/core";
import {Page} from "../../navigation/page";
import {ButtonComponent} from "../../buttons/button/button.component";
import {ButtonProminence} from "../../buttons/button/button";
import {FooterMessageComponent} from "../../notification/message/footer-message.component";
import {MessageLevel} from "../../notification/message/message";
import {DealModel, DealResult, DealResultCode} from "../deal.model";
import {FormComponent} from '../../inputs/form/form.component';
import {TextFieldComponent} from '../../inputs/text-field/text-field.component';
import {ModalPageLayoutComponent} from '../../page-layout/modal-page-layout/modal-page-layout.component';
import {AppliedDealsPanelComponent} from "../applied-deals-panel/applied-deals-panel.component";
import {FormsModule} from '@angular/forms';
import {PageFooterControlDirective} from '../../page-layout/page-footer-control.directive';
import {IdentityModel} from '../../identity/identity.model';

@Component({
    selector: "woa-deal-code-entry-page",
    templateUrl: "./deal-code-entry-page.component.html",
    styleUrl: "./deal-code-entry-page.component.scss",
    providers: [{provide: Page, useExisting: DealCodeEntryPageComponent}],
    imports: [
        AppliedDealsPanelComponent,
        ButtonComponent,
        FooterMessageComponent,
        FormComponent,
        FormsModule,
        ModalPageLayoutComponent,
        PageFooterControlDirective,
        TextFieldComponent
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class DealCodeEntryPageComponent extends Page {
    public dealModel: DealModel = inject(DealModel);
    private identityModel: IdentityModel = inject(IdentityModel);

    ButtonProminence = ButtonProminence;
    DealStatus = DealResultCode;
    MessageLevel = MessageLevel;

    public readonly form: Signal<FormComponent> = viewChild.required(FormComponent);

    ordering: WritableSignal<boolean> = signal<boolean>(false);
    result: WritableSignal<DealResult> = signal<DealResult>(undefined);

    public readonly showCode: Signal<boolean> = computed<boolean>(() =>
        this.identityModel.dealCodesAvailable() && !this.dealModel.dealLimitReached() &&
        (!this.result() || this.result().resultCode === DealResultCode.DECLINED || this.result().resultCode === DealResultCode.UNAVAILABLE));

    public readonly codeInputMode: Signal<"text" | "numeric"> = computed<"text" | "numeric">(() =>
        this.identityModel.currentMember().anySelectByNonNumericDealCodeInApp ? "text" : "numeric");

    async dealRemoved(): Promise<void> {
        if (!this.dealModel.appliedDeals().length && !this.dealModel.dealCodesAvailable()) {
            void this.dismiss();
        }
    }

    dealCode: string;

    async applyDealCode(): Promise<void> {
        this.dealCode = this.dealCode.trim();

        this.result.set(undefined);
        if (!this.form().valid) {
            return;
        }

        this.ordering.set(true);
        try {
            this.result.set(await this.dealModel.orderDealByDealCode(this.dealCode));
            if (this.result().resultCode === DealResultCode.IMMEDIATE || this.result().resultCode === DealResultCode.DEFERRED) {
                void this.dismiss(this.result());
            }
        } finally {
            this.ordering.set(false);
        }
    }
}
